import React, { useState, useEffect } from "react";
import { GrFormNext, GrFormPrevious } from "react-icons/gr";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const campusFacilities = [
  {
    src: "/renamed_website_images/auditorium-2.jpg",
    title: "School Auditorium",
    desc: "Modern auditorium facility for assemblies, events, and cultural performances.",
  },
  {
    src: "/renamed_website_images/campus-038.jpeg",
    title: "Campus Infrastructure & Gardens",
    desc: "Clean, spacious school environment designed for student growth and outdoor learning.",
  },
  {
    src: "/renamed_website_images/campus-041.jpeg",
    title: "School Grounds & Pathways",
    desc: "Lush green walkways and vibrant campus surroundings.",
  },
  {
    src: "/renamed_website_images/gallery-005.jpg",
    title: "Interactive Learning Spaces",
    desc: "Engaging classroom setup fostering collaboration and intellectual growth.",
  },
  {
    src: "/renamed_website_images/infrastructure-section-mobile.jpg",
    title: "School Building & Facilities",
    desc: "Well-maintained academic blocks with safe, modern infrastructure.",
  },
  {
    src: "/renamed_website_images/sports-activities-001.jpg",
    title: "Sports Arena & Playground",
    desc: "Dedicated sports area for athletic training, physical education, and outdoor games.",
  },
];

const Campus = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const next = () => setCurrentIndex((p) => (p + 1) % campusFacilities.length);
  const prev = () =>
    setCurrentIndex((p) => (p === 0 ? campusFacilities.length - 1 : p - 1));

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative py-24 px-6 md:px-16 overflow-hidden bg-[#020810]">
      {/* Background Decor */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full opacity-30 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-blue-600/10 blur-[100px] rounded-full" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-gold/5 blur-[100px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* ── Text Content ── */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:w-1/2"
          >
            <span className="section-badge mb-6">Our Environment</span>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-8 leading-tight"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              World-Class <span className="text-gold">Campus</span> Facilities
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-10 max-w-xl">
              Our campus is designed to inspire creativity, foster collaboration, and provide students with modern tools to excel. From advanced science labs to vibrant recreational spaces, every corner of Galaxy Star is built for learning.
            </p>

            <div className="grid grid-cols-2 gap-6 mb-10 text-gray-300">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-gold" />
                <span className="font-bold uppercase tracking-widest text-xs">
                  Smart Classrooms
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-gold" />
                <span className="font-bold uppercase tracking-widest text-xs">
                  Modern Science Labs
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-gold" />
                <span className="font-bold uppercase tracking-widest text-xs">
                  Rich Library
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-gold" />
                <span className="font-bold uppercase tracking-widest text-xs">
                  Sports Arena
                </span>
              </div>
            </div>

            <Link
              to="/gallery"
              className="cosmic-btn-gold px-10 py-4 rounded-full text-sm uppercase tracking-widest inline-block"
            >
              Take a Virtual Tour
            </Link>
          </motion.div>

          {/* ── Visual Section (Smooth Slider) ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:w-1/2 w-full"
          >
            <div className="relative p-3 border border-white/10 rounded-[2rem] bg-white/5 backdrop-blur-md">
              <div className="relative rounded-[1.5rem] overflow-hidden shadow-2xl aspect-[4/3] bg-[#040b1f]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    className="w-full h-full relative"
                  >
                    <img
                      src={campusFacilities[currentIndex].src}
                      alt={campusFacilities[currentIndex].title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#020810]/90 via-transparent to-transparent" />
                    
                    {/* Caption */}
                    <div className="absolute bottom-6 left-6 right-6">
                      <h4 className="text-gold font-bold text-base md:text-lg mb-1" style={{ fontFamily: "Cinzel, serif" }}>
                        {campusFacilities[currentIndex].title}
                      </h4>
                      <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
                        {campusFacilities[currentIndex].desc}
                      </p>
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 flex items-center justify-between pointer-events-none z-20">
                  <button
                    onClick={prev}
                    className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-gold hover:text-black transition-all pointer-events-auto shadow-lg"
                  >
                    <GrFormPrevious size={22} />
                  </button>
                  <button
                    onClick={next}
                    className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-gold hover:text-black transition-all pointer-events-auto shadow-lg"
                  >
                    <GrFormNext size={22} />
                  </button>
                </div>

                {/* Progress Indicators */}
                <div className="absolute top-4 right-4 flex gap-2 z-20 bg-black/40 px-3 py-1.5 rounded-full backdrop-blur-md border border-white/10">
                  {campusFacilities.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentIndex(i)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        i === currentIndex ? "w-6 bg-gold" : "w-2 bg-white/40"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Campus;
